import { onCLS, onFCP, onLCP, onTTFB } from 'web-vitals';
import { logger } from '../../core/utils/logger';

export interface PerformanceMetrics {
  cls: number | null;
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
  timestamp: number;
}

export interface ComponentPerformanceData {
  componentName: string;
  renderTime: number;
  updateCount: number;
  lastUpdate: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics = {
    cls: null,
    fcp: null,
    lcp: null,
    ttfb: null,
    timestamp: Date.now()
  };

  private componentMetrics: Map<string, ComponentPerformanceData> = new Map();
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initializeWebVitals();
    this.initializePerformanceObserver();
  }

  private initializeWebVitals() {
    if (typeof window !== 'undefined') {
      onCLS((metric) => {
        this.metrics.cls = metric.value;
        this.reportMetric('CLS', metric.value);
      });



      onFCP((metric) => {
        this.metrics.fcp = metric.value;
        this.reportMetric('FCP', metric.value);
      });

      onLCP((metric) => {
        this.metrics.lcp = metric.value;
        this.reportMetric('LCP', metric.value);
      });

      onTTFB((metric) => {
        this.metrics.ttfb = metric.value;
        this.reportMetric('TTFB', metric.value);
      });
    }
  }

  private initializePerformanceObserver() {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'measure') {
            this.handleMeasureEntry(entry as PerformanceMeasure);
          }
        }
      });

      observer.observe({ entryTypes: ['measure'] });
      this.observers.push(observer);
    }
  }

  private handleMeasureEntry(entry: PerformanceMeasure) {
    if (entry.name.startsWith('⚛️')) {
      const componentName = this.extractComponentName(entry.name);
      if (componentName) {
        this.updateComponentMetrics(componentName, entry.duration);
      }
    }
  }

  private extractComponentName(measureName: string): string | null {
    const match = measureName.match(/⚛️ (.+?) \(/);
    return match ? match[1] : null;
  }

  private updateComponentMetrics(componentName: string, renderTime: number) {
    const existing = this.componentMetrics.get(componentName);
    if (existing) {
      existing.renderTime = renderTime;
      existing.updateCount += 1;
      existing.lastUpdate = Date.now();
    } else {
      this.componentMetrics.set(componentName, {
        componentName,
        renderTime,
        updateCount: 1,
        lastUpdate: Date.now()
      });
    }
  }

  private reportMetric(name: string, value: number) {
    if (process.env.NODE_ENV === 'development') {
      logger.debug(`[Performance] ${name}: ${value}`);
    }
    
    this.sendToAnalytics(name, value);
  }

  private sendToAnalytics(metricName: string, value: number) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'web_vitals', {
        event_category: 'Performance',
        event_label: metricName,
        value: Math.round(value),
        non_interaction: true
      });
    }
  }

  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  public getComponentMetrics(): ComponentPerformanceData[] {
    return Array.from(this.componentMetrics.values());
  }

  public getSlowComponents(threshold: number = 16): ComponentPerformanceData[] {
    return this.getComponentMetrics().filter(
      component => component.renderTime > threshold
    );
  }

  public startMeasure(name: string) {
    if (typeof window !== 'undefined' && window.performance) {
      window.performance.mark(`${name}-start`);
    }
  }

  public endMeasure(name: string) {
    if (typeof window !== 'undefined' && window.performance) {
      window.performance.mark(`${name}-end`);
      window.performance.measure(name, `${name}-start`, `${name}-end`);
    }
  }

  public measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    this.startMeasure(name);
    return fn().finally(() => {
      this.endMeasure(name);
    });
  }

  public cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.componentMetrics.clear();
  }
}

export const performanceMonitor = new PerformanceMonitor();

export const usePerformanceMonitoring = () => {
  return {
    startMeasure: performanceMonitor.startMeasure.bind(performanceMonitor),
    endMeasure: performanceMonitor.endMeasure.bind(performanceMonitor),
    measureAsync: performanceMonitor.measureAsync.bind(performanceMonitor),
    getMetrics: performanceMonitor.getMetrics.bind(performanceMonitor),
    getComponentMetrics: performanceMonitor.getComponentMetrics.bind(performanceMonitor),
    getSlowComponents: performanceMonitor.getSlowComponents.bind(performanceMonitor)
  };
};

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
